import React, { useEffect, useState, lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useSelector } from "react-redux";
import LoginPage from "../pages/auth/LoginPage";
import DashboardLayout from "../components/layout/DashboardLayout";
import ProtectedRoute from "./ProtectedRoute";
import PublicRoute from "./PublicRoute";
import PageUnderConstruction from "../components/common/PageUnderConstruction";
import RoleProtectedRoute from "./RoleProtectedRoute";

// Pre-define lazy-loaded components
const RecordingsPage = lazy(() => import("../pages/recordings/RecordingsPage"));
const Dashboard = lazy(() => import("../pages/dashboard/NewDashboard"));
const PDFGeneratorPage = lazy(() => import("../pages/dashboard/PDFGeneratorPage"));
const ViewPermissions = lazy(() =>
  import("../pages/userManagement/ViewPermissions")
);
const CreateNewRole = lazy(() =>
  import("../pages/userManagement/CreateNewRole")
);
const RolesPage = lazy(() => import("../pages/userManagement/RolesPage"));
const UsersPage = lazy(() => import("../pages/userManagement/CreateNewUser"));
const PermissionsPage = lazy(() =>
  import("../pages/userManagement/PermissionsPage")
);
const AuditPage = lazy(() => import("../pages/userManagement/AuditPage"));
const ViewModifyRole = lazy(() =>
  import("../pages/userManagement/ModifyRolesPage")
);
const ChangePasswordPage = lazy(() =>
  import("../pages/userManagement/ChangePasswordPage")
);
const ResetPasswordPage = lazy(() =>
  import("../pages/userManagement/ResetPasswordPage")
);

const CreateNewUser = lazy(() =>
  import("../pages/userManagement/CreateNewUser")
);
const ViewModifyUser = lazy(() =>
  import("../pages/userManagement/ModifyUsersPage")
);
const LOBManagementPage = lazy(() =>
  import("../pages/screenBuilder/LOBManagementPage")
);
const LOBFormPage = lazy(() =>
  import("../pages/screenBuilder/LOBFormPage")
);
const ProductManagementPage = lazy(() =>
  import("../pages/screenBuilder/ProductManagementPage")
);
const ProductFormPage = lazy(() =>
  import("../pages/screenBuilder/ProductFormPage")
);

const PublicLeadForm = lazy(() =>
  import("../pages/leadManagement/PublicLeadForm")
);
const PublicDUQRequestForm = lazy(() =>
  import("../pages/leadManagement/PublicDUQRequestForm")
);
const FourOaksSuppLeadForm = lazy(() =>
  import("../pages/leadManagement/FourOaksSuppLeadForm")
);
const PreviewDUQForm = lazy(() =>
  import("../pages/duq/PreviewDUQForm")
);
const ReviewDUQForm = lazy(() =>
  import("../pages/duq/ReviewDUQForm")
);
const TemplateBuilderPage = lazy(() =>
  import("../pages/templateBuilder/TemplateBuilderPage")
);
const TemplateBuilderStandalone = lazy(() =>
  import("../pages/templateBuilder/TemplateBuilderStandalone")
);
const InviteNewUser = lazy(() =>
  import("../pages/userManagement/InviteNewUser")
);
const TemplatePreviewPage = lazy(() =>
  import("../pages/templateBuilder/TemplatePreviewPage")
);
const TemplatePreviewStepperPage = lazy(() =>
  import("../pages/templateBuilder/TemplatePreviewStepperPage")
);
const SectionBuilderStandalone = lazy(() =>
  import("../pages/sectionBuilder/SectionBuilderStandalone")
);
const SectionTemplatesPage = lazy(() =>
  import("../pages/sectionBuilder/SectionTemplatesPage")
);
const CreateSectionForm = lazy(() =>
  import("../pages/sectionBuilder/CreateSectionForm")
);
const ProductSelectionPage = lazy(() =>
  import("../pages/templateBuilder/ProductSelectionPage")
);
const QuestionConfigurationPage = lazy(() =>
  import("../pages/questionConfiguration/QuestionConfigurationPage")
);
const SimplifiedBulkUpload = lazy(() =>
  import("../pages/userManagement/SimplifiedBulkUpload")
);
const MastersPage = lazy(() =>
  import("../pages/masterManagement/MastersPage")
);
const CreateNewMaster = lazy(() =>
  import("../pages/masterManagement/CreateNewMasters")
);
const ViewModifyMaster = lazy(() =>
  import("../pages/masterManagement/ViewModifyMasters")
);
const DepartmentMaster = lazy(() =>
  import("../pages/masterManagement/DepartmentMaster")
);
const CustomizeQuestionnaire = lazy(() =>
  import("../pages/duq/CustomizeQuestionnaire")
);
// const LandingPage = lazy(() =>
//   import("../pages/landing/LandingPage")
// );

const CreateTemplate = lazy(() => import("../pages/templateBuilder/CreateTemplatePage"));
const ViewTemplate = lazy(() => import("../pages/templateBuilder/ViewTemplate"));
const CreateRequestPage = lazy(() => import("../pages/dashboard/CreateRequestPage"));
const ViewDUQPage = lazy(() => import("../pages/dashboard/ViewDUQPage"));
const PdfTemplateMapping = lazy(() =>
  import("../pages/documentManagement/PdfTemplateMapping")
);
/**
 * Get component by menu code
 * Maps menu codes to pre-defined lazy-loaded components
 */
const getComponentByMenuCode = (menuCode) => {
  const componentMap = {
    RECORDINGS: RecordingsPage,
    DASHBOARD: Dashboard,
    PDF_GENERATOR: PDFGeneratorPage,
    VIEW_PERMISSIONS: ViewPermissions,
    CREATE_NEW_ROLE: CreateNewRole,
    ROLES: RolesPage,
    USERS: UsersPage,
    PERMISSIONS: PermissionsPage,
    AUDIT_TRAIL_USERMANAGEMENT: AuditPage,
    VIEW_MODIFY_ROLE: ViewModifyRole,
    CHANGE_PASSWORD: ChangePasswordPage,
    RESET_PASSWORD: ResetPasswordPage,
    CREATE_NEW_USER: CreateNewUser,
    INVITE_NEW_USER: InviteNewUser,
    VIEW_MODIFY_USER: ViewModifyUser,
    LOB_MANAGEMENT: LOBManagementPage,
    PRODUCT_MANAGEMENT: ProductManagementPage,
    TEMPLATE_BUILDER: ProductSelectionPage,
    SECTION_TEMPLATES: SectionTemplatesPage,
    SCREEN_BUILDER: PageUnderConstruction,
    CREATE_NEW_MASTER: MastersPage,
    VIEW_MODIFY_MASTER: CreateNewMaster,
    DEPARTMENT_MASTER: DepartmentMaster,
    CREATE_TEMPLATE: CreateTemplate,
    VIEW_TEMPLATE: ViewTemplate,
    PDF_TEMPLATE_MAPPING: PdfTemplateMapping,
  };

  // Return the component if it exists in the map, otherwise return PageUnderConstruction
  return componentMap[menuCode] || PageUnderConstruction;
};

/**
 * Root redirect component that redirects based on authentication and permissions
 */
const RootRedirect = () => {
  const { isAuthenticated } = useSelector((state) => state.auth);
  const { menuPermissions } = useSelector((state) => state.permissions);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Find the first valid route from permissions
  if (menuPermissions && menuPermissions.length > 0) {
    const findFirstValidRoute = (items) => {
      for (const item of items) {
        // Check both route_url and path properties
        const routeUrl = item.route_url || item.path;
        if (routeUrl && !routeUrl.startsWith("#")) {
          return routeUrl;
        }
        if (item.children && item.children.length > 0) {
          const childRoute = findFirstValidRoute(item.children);
          if (childRoute) return childRoute;
        }
      }
      return null;
    };

    const firstRoute = findFirstValidRoute(menuPermissions);
    if (firstRoute) {
      return <Navigate to={firstRoute} replace />;
    }
  }

  // Default fallback
  return <Navigate to="/dashboard" replace />;
};

/**
 * Main AppRoutes component with fully dynamic routing
 */
const AppRoutes = () => {
  const { menuPermissions, isLoading } = useSelector((state) => state.permissions);
  const [dynamicRoutes, setDynamicRoutes] = useState([]);

  useEffect(() => {
    if (!menuPermissions || menuPermissions.length === 0) {
      setDynamicRoutes([]);
      return;
    }

    /**
     * Recursively process menu items to generate routes
     */
    const processMenuItems = (items, parentPath = "") => {
      if (!items || !Array.isArray(items)) return [];

      const routes = [];

      items.forEach((menu) => {
        // Extract menu configuration
        const menuConfig = menu.am_menu_configuration || menu;
        const menuCode = menuConfig.menu_code || menu.menu_code || menu.id;
        const routeUrl = menuConfig.route_url || menu.route_url || menu.path;
        const menuId = menuConfig.menu_id || menu.menu_id || menu.menuId;

        // Skip only inactive menus
        // Note: is_restricted means the menu is exclusive to this role, but the user with this role should still access it
        if (!menu.is_active) {
          return;
        }

        // Handle routes with valid URLs
        if (routeUrl && !routeUrl.startsWith("#")) {
          const DynamicComponent = getComponentByMenuCode(menuCode);

          // Normalize route URL - ensure it starts with / and handle /dashboard prefix
          let normalizedRouteUrl = routeUrl;

          // If route is exactly "/dashboard", keep it as is
          if (routeUrl === '/dashboard') {
            normalizedRouteUrl = '/dashboard';
          }
          // If route starts with /dashboard/ (with trailing content), remove the /dashboard prefix
          else if (routeUrl.startsWith('/dashboard/')) {
            normalizedRouteUrl = routeUrl.replace('/dashboard', '');
          }
          // Otherwise use the route as is
          else if (!routeUrl.startsWith('/')) {
            normalizedRouteUrl = '/' + routeUrl;
          }

          const route = (
            <Route
              key={`route-${menuId}-${normalizedRouteUrl}`}
              path={normalizedRouteUrl}
              element={
                <RoleProtectedRoute menuId={menuId} menuCode={menuCode}>
                  <DashboardLayout>
                    <Suspense
                      fallback={
                        <div className="loading-spinner">Loading...</div>
                      }
                    >
                      <DynamicComponent />
                    </Suspense>
                  </DashboardLayout>
                </RoleProtectedRoute>
              }
            />
          );

          routes.push(route);

          // Don't add wildcard routes - let child routes handle their own paths
          // This prevents route conflicts and allows proper nested routing
        }
        // Handle hash routes or menus without URLs
        else if (routeUrl && routeUrl.startsWith("#")) {
          const constructionPath = `/page-under-construction/${menuCode.toLowerCase()}`;
          const route = (
            <Route
              key={`construction-${menuId}-${menuCode}`}
              path={constructionPath}
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <PageUnderConstruction />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
          );
          routes.push(route);
        }

        // Process children recursively
        if (menu.children && menu.children.length > 0) {
          const childRoutes = processMenuItems(
            menu.children,
            routeUrl || parentPath
          );
          routes.push(...childRoutes);
        }
      });

      return routes;
    };

    const generatedRoutes = processMenuItems(menuPermissions);
    setDynamicRoutes(generatedRoutes);

  }, [menuPermissions]);

  return (
    <Routes>
      {/* Landing page - disabled, redirecting directly to login */}
      {/* <Route
        path="/landing"
        element={
          <Suspense fallback={<div className="loading-spinner">Loading...</div>}>
            <LandingPage />
          </Suspense>
        }
      /> */}

      {/* Public routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />

      {/* Public Lead Form - No authentication required */}
      <Route
        path="/lead/link/:linkToken"
        element={
          <Suspense fallback={<div className="loading-spinner">Loading...</div>}>
            <PublicLeadForm />
          </Suspense>
        }
      />

      {/* Four Oaks Supplement Lead Form - No authentication required */}
      <Route
        path="/lead/supp-link/:linkToken"
        element={
          <Suspense fallback={<div className="loading-spinner">Loading...</div>}>
            <FourOaksSuppLeadForm />
          </Suspense>
        }
      />

      {/* Public DUQ Form - No authentication required */}
      <Route
        path="/duq/:requestId"
        element={
          <Suspense fallback={<div className="loading-spinner">Loading...</div>}>
            <PublicLeadForm />
          </Suspense>
        }
      />

      {/* Public DUQ Request Form - Beautiful form without authentication */}
      <Route
        path="/request-quote"
        element={
          <Suspense fallback={<div className="loading-spinner">Loading...</div>}>
            <PublicDUQRequestForm />
          </Suspense>
        }
      />

      {/* Public User Invitation Signup - No authentication required */}
      <Route
        path="/user-management/create-new-user"
        element={
          <Suspense fallback={<div className="loading-spinner">Loading...</div>}>
            <CreateNewUser />
          </Suspense>
        }
      />

      {/* Edit User Route - Includes userId in URL for refresh support */}
      <Route
        path="/user-management/edit-user/:userId"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Suspense fallback={<div className="loading-spinner">Loading...</div>}>
                <CreateNewUser />
              </Suspense>
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Root route with smart redirection */}
      <Route path="/" element={<RootRedirect />} />

      {/* Page under construction route */}
      <Route
        path="/page-under-construction/:id"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <PageUnderConstruction />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Dynamically generated routes based on user permissions */}
      {dynamicRoutes}

      {/* Screen Builder - LOB Management Routes */}
      <Route
        path="/lob-management/create"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Suspense fallback={<div className="loading-spinner">Loading...</div>}>
                <LOBFormPage />
              </Suspense>
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/lob-management/edit/:lobId"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Suspense fallback={<div className="loading-spinner">Loading...</div>}>
                <LOBFormPage />
              </Suspense>
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Screen Builder - Product Management Routes */}
      <Route
        path="/product-management/create"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Suspense fallback={<div className="loading-spinner">Loading...</div>}>
                <ProductFormPage />
              </Suspense>
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/product-management/edit/:productId"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Suspense fallback={<div className="loading-spinner">Loading...</div>}>
                <ProductFormPage />
              </Suspense>
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/product-management/view/:productId"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Suspense fallback={<div className="loading-spinner">Loading...</div>}>
                <ProductFormPage />
              </Suspense>
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Question Configuration Hub - Common for all products */}
      <Route
        path="/question-configuration"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Suspense fallback={<div className="loading-spinner">Loading...</div>}>
                <QuestionConfigurationPage />
              </Suspense>
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Template Builder Routes */}
      <Route
        path="/template-builder"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Suspense fallback={<div className="loading-spinner">Loading...</div>}>
                <ProductSelectionPage />
              </Suspense>
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      {/* Template Builder Canvas - Inside Layout */}
      <Route
        path="/template-builder/:productId"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Suspense fallback={<div className="loading-spinner">Loading...</div>}>
                <TemplateBuilderStandalone />
              </Suspense>
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/template-preview"
        element={
          <Suspense fallback={<div className="loading-spinner">Loading...</div>}>
            <TemplatePreviewPage />
          </Suspense>
        }
      />
      <Route
        path="/template-preview-stepper"
        element={
          <Suspense fallback={<div className="loading-spinner">Loading...</div>}>
            <TemplatePreviewStepperPage />
          </Suspense>
        }
      />

      {/* Create Request Full Page Route */}
      <Route
        path="/create-request"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Suspense fallback={<div className="loading-spinner">Loading...</div>}>
                <CreateRequestPage />
              </Suspense>
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* View Application Details Full Page Route */}
      <Route
        path="/view-application/:requestId"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Suspense fallback={<div className="loading-spinner">Loading...</div>}>
                <ViewDUQPage />
              </Suspense>
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Template Management Routes */}
      <Route path="/view-template" element={<Navigate to="/create-template" replace />} />
      <Route
        path="/create-template"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Suspense fallback={<div className="loading-spinner">Loading...</div>}>
                <CreateTemplate />
              </Suspense>
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Section Templates Management */}
      <Route
        path="/section-templates"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Suspense fallback={<div className="loading-spinner">Loading...</div>}>
                <SectionTemplatesPage />
              </Suspense>
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Section Builder Routes */}
      {/* Create Section Form - Inside Layout (CRUD) */}
      <Route
        path="/section-builder/new"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Suspense fallback={<div className="loading-spinner">Loading...</div>}>
                <CreateSectionForm />
              </Suspense>
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      {/* Section Builder Canvas - Inside Layout */}
      <Route
        path="/section-builder/:sectionId"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Suspense fallback={<div className="loading-spinner">Loading...</div>}>
                <SectionBuilderStandalone />
              </Suspense>
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Simplified Bulk Upload Route */}
      <Route
        path="/bulk-upload/simplified"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Suspense fallback={<div className="loading-spinner">Loading...</div>}>
                <SimplifiedBulkUpload />
              </Suspense>
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Department Master Route */}
      <Route
        path="/master-management/department"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Suspense fallback={<div className="loading-spinner">Loading...</div>}>
                <DepartmentMaster />
              </Suspense>
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* DUQ Questionnaire Customization Route */}
      <Route
        path="/pdf-template-mapping"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Suspense fallback={<div className="loading-spinner">Loading...</div>}>
                <PdfTemplateMapping />
              </Suspense>
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* DUQ Questionnaire Customization Route */}
      <Route
        path="/duq/customize"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Suspense fallback={<div className="loading-spinner">Loading...</div>}>
                <CustomizeQuestionnaire />
              </Suspense>
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* DUQ Preview Route - Uses PublicLeadForm with stepper */}
      <Route
        path="/duq/preview"
        element={
          <ProtectedRoute>
            <Suspense fallback={<div className="loading-spinner">Loading...</div>}>
              <PreviewDUQForm />
            </Suspense>
          </ProtectedRoute>
        }
      />

      {/* DUQ Review Route - For reviewing submitted applications */}
      <Route
        path="/duq/review/:requestId"
        element={
          <ProtectedRoute>
            <Suspense fallback={<div className="loading-spinner">Loading...</div>}>
              <ReviewDUQForm />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/duq/review/app/:applicationNo"
        element={
          <ProtectedRoute>
            <Suspense fallback={<div className="loading-spinner">Loading...</div>}>
              <ReviewDUQForm />
            </Suspense>
          </ProtectedRoute>
        }
      />

      {/* Catch-all route for unauthorized or non-existent routes */}
      <Route
        path="*"
        element={
          isLoading ? (
            <div className="loading-spinner">Loading...</div>
          ) : (
            <ProtectedRoute>
              <DashboardLayout>
                <PageUnderConstruction />
              </DashboardLayout>
            </ProtectedRoute>
          )
        }
      />
    </Routes>
  );
};

export default AppRoutes;
